import type { ReactNode, SyntheticEvent } from 'react'
import { Button, Checkbox, ConfidenceTicks, MetricTable, Tag, Text } from '@grovus/design-system'
import type { MetricSection } from '@grovus/design-system'
import { columnDefs, type ColumnKey } from './columns'
import { fill, money, num, percent, ratio } from './format'
import type { Row, SortKey } from './partition'
import { sortRows } from './partition'
import { TagChips, type Tagging } from './TagChips'
import { adjustedPartial } from './metrics'
import {
  AccountMarks, audienceCell, glyph, sortLabels, verdictQualifier, qualifierText, conversionUnit, formatLabel,
  markTally, PartialLegend, PausedMark, reasonSentence, VerdictMark, floorStruck, type ViewContext,
} from './view'

interface Props {
  ctx: ViewContext
  rows: Row[]
  /**
   * The visible columns in display order, resolved by the screen through
   * `visibleColumns`. Every cell is still built, so hiding a column changes the columns
   * and nothing else: the sort and the totals never read this.
   */
  columns: ColumnKey[]
  sort: SortKey
  dir: 'asc' | 'desc'
  onSort: (key: SortKey) => void
  onOpenCreative: (id: string) => void
  showSamples: boolean
  onWidenPeriod?: (days: 30 | 90) => void
  /** Whether a window of `days` is available at all. A range widens backwards from its
   * end, so it cannot widen past the stored history; a preset always can. */
  canWiden?: (days: 30 | 90) => boolean
  /**
   * The bulk assign selection (GRO-157), absent for a reader who cannot assign. The
   * screen owns it; the table draws a checkbox in each identity cell and reports toggles.
   */
  selection?: {
    picked: ReadonlySet<string>
    enabled: boolean
    onToggle: (id: string) => void
    /** The checkbox's accessible name for a row. */
    label: (row: Row) => string
  }
  /** In-cell assignment of the idea and the visual (GRO-170), absent like `selection`. */
  tagging?: Tagging
}

/**
 * A press on the checkbox, or a key on it, stays with the checkbox. The row opens the
 * detail on a click and on Enter or Space, and a Mantine checkbox is an input inside a
 * label: a press on either bubbles to the row before the input has changed.
 */
const keep = (event: SyntheticEvent) => event.stopPropagation()

/**
 * The window a creative's own rate says would clear the reliable line, if there is one.
 *
 * Only offered when the projection is plausible inside a window the product actually has:
 * a row that would need a year is not told to widen, it is told nothing.
 */
export function widenTo(
  row: Row,
  periodDays: number,
  reliableAt: number,
  canWiden: (days: 30 | 90) => boolean = () => true,
): 30 | 90 | null {
  const conversions = row.metrics.conversions ?? 0
  if (conversions <= 0 || conversions >= reliableAt || periodDays >= 90) return null
  const perDay = conversions / periodDays
  for (const days of [30, 90] as const) {
    if (days > periodDays && perDay * days >= reliableAt) return canWiden(days) ? days : null
  }
  return null
}

/** Two arrows pointing apart: widen the period. Drawn in the text colour, so it follows both palettes. */
function WidenGlyph() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
      <path d="M1.5 8h13M4.5 5 1.5 8l3 3M11.5 5l3 3-3 3" />
    </svg>
  )
}

export function CreativesTable({
  ctx, rows, columns: visible, sort, dir, onSort, onOpenCreative, showSamples, onWidenPeriod, canWiden, selection, tagging,
}: Props) {
  const { t, lang, currency } = ctx

  const defs = columnDefs(t, ctx.thresholds)
  const columns = visible.map((key) => defs[key])
  // Every cell is built through the tally, so the legend explains exactly the marks the
  // visible columns show.
  const mark = markTally(t)

  function creativeCell(row: Row): ReactNode {
    const conversions = row.metrics.conversions ?? 0
    return (
      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {/* Inside the identity cell, not a column of its own: the shared table pins only
            its first column, and this keeps the creative pinned with its checkbox. */}
        {selection && (
          <span onClick={keep} onKeyDown={keep} onKeyUp={keep} style={{ flex: 'none', display: 'inline-flex' }}>
            <Checkbox
              aria-label={selection.label(row)}
              checked={selection.picked.has(row.id)}
              disabled={!selection.enabled}
              onChange={() => selection.onToggle(row.id)}
              data-testid={`select-${row.id}`}
            />
          </span>
        )}
        {row.thumbnailUrl !== null ? (
          <img
            src={row.thumbnailUrl}
            loading="lazy"
            alt=""
            data-testid="creative-thumbnail"
            style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 4, flex: 'none' }}
          />
        ) : (
          <Tag tt="none" size="sm" data-testid="creative-format">{formatLabel(row.format, t)}</Tag>
        )}
        <span>
          <Text size="md" component="span">{row.displayName}</Text>
          <PausedMark at={row.pausedSince} t={t} lang={ctx.lang} />
          {showSamples && (
            <Text size="xs" className="grv-muted" component="div">
              {fill(t.sampleAt, { n: conversions, u: conversionUnit(conversions, ctx) })}
            </Text>
          )}
        </span>
      </span>
    )
  }

  function verdictCell(row: Row): ReactNode {
    const widen = onWidenPeriod ? widenTo(row, ctx.periodDays, ctx.thresholds.reliableAt, canWiden) : null
    const qualifier = verdictQualifier(row, t)
    return (
      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <ConfidenceTicks
          conversions={row.metrics.conversions ?? 0}
          thresholds={ctx.thresholds}
          progress
        />
        <VerdictMark row={row} t={t} />
        {widen !== null && onWidenPeriod && (
          <Button
            variant="subtle"
            size="compact-xs"
            aria-label={fill(t.widenTo, { n: widen })}
            title={fill(t.widenTitle, { n: widen })}
            onClick={(event) => {
              event.stopPropagation()
              onWidenPeriod(widen)
            }}
          >
            <WidenGlyph />
          </Button>
        )}
        {qualifier && <Text size="xs" className="grv-muted" component="span">{qualifierText(qualifier)}</Text>}
      </span>
    )
  }

  function roasCell(row: Row): ReactNode {
    const body = floorStruck(ratio(row.derived.roas.value, lang), row.derived.roas.value !== null, row, ctx)
    return mark.at('roas')(body, row.derived.roas.partial)
  }

  function adjCell(row: Row): ReactNode {
    const body = floorStruck(ratio(row.adjustedRoas, lang), row.adjustedRoas !== null, row, ctx)
    return mark.at('adj')(body, adjustedPartial(row))
  }

  function toRow(row: Row): Record<string, ReactNode> {
    mark.lagging('accounts', row, ctx)
    return {
      // A plain string the table can key on: the first column is a composed node, and
      // `displayName` is a label two different assets can share.
      id: row.id,
      name: creativeCell(row),
      accounts: <AccountMarks row={row} ctx={ctx} />,
      format: <span data-testid={`format-${row.id}`}>{formatLabel(row.format, t)}</span>,
      hypothesis: <TagChips row={row} ctx={ctx} tagging={tagging} />,
      audience: <span data-testid={`audience-${row.id}`}>{audienceCell(row, ctx.dimensions.audiences, lang, t)}</span>,
      spend: mark.at('spend')(money(row.metrics.spend, currency, lang), row.metrics.incomplete.includes('spend')),
      share: (
        <span data-testid="row-share">
          {row.share.pct === null ? glyph : mark.at('share')(`${row.share.pct}%`, row.share.partial)}
        </span>
      ),
      hook: mark.at('hook')(percent(row.derived.hookRate.value, lang, 1), row.derived.hookRate.partial),
      hold: mark.at('hold')(percent(row.derived.holdRate.value, lang, 1), row.derived.holdRate.partial),
      ctr: mark.at('ctr')(percent(row.derived.ctr.value, lang), row.derived.ctr.partial),
      purch: mark.at('purch')(num(row.metrics.conversions, lang), row.metrics.incomplete.includes('conversions')),
      roas: roasCell(row),
      adj: adjCell(row),
      cpa: mark.at('cpa')(
        floorStruck(money(row.derived.cpa.value, currency, lang, 2), row.derived.cpa.value !== null, row, ctx),
        row.derived.cpa.partial,
      ),
      aov: mark.at('aov')(
        floorStruck(money(row.derived.aov.value, currency, lang, 2), row.derived.aov.value !== null, row, ctx),
        row.derived.aov.partial,
      ),
      cpc: mark.at('cpc')(money(row.derived.cpc.value, currency, lang, 2), row.derived.cpc.partial),
      cpm: mark.at('cpm')(money(row.derived.cpm.value, currency, lang, 2), row.derived.cpm.partial),
      activeDays: mark.at('activeDays')(num(row.activeDays, lang), row.metrics.incomplete.includes('impressions')),
      ads: num(row.ads.length, lang),
      verdict: (
        <span title={reasonSentence(row, ctx)} data-testid={`verdict-${row.id}`}>
          {verdictCell(row)}
        </span>
      ),
    }
  }

  const sections: MetricSection[] = [{ key: 'main', rows: sortRows(rows, sort, dir, sortLabels(ctx)).map(toRow) }]

  return (
    <div data-testid="creatives-table">
      <PartialLegend kinds={mark.kinds(visible)} t={t} />
      <MetricTable
        columns={columns}
        sections={sections}
        rowKey="id"
        caption={t.tableCaption}
        sort={{ key: sort, dir }}
        onSort={(key) => {
          const column = columns.find((candidate) => candidate.key === key)
          if (column) onSort(column.key)
        }}
        onRowClick={onOpenCreative}
        scrollHint={t.tableScrollHint}
      />
    </div>
  )
}
